import { AlertCircle } from "@tamagui/lucide-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  YStack,
  XStack,
  Circle,
  getTokenValue,
  Token,
  HeadingProps,
  ParagraphProps,
} from "tamagui";
import {
  GradientButton,
  GradientButtonProps,
  OutlinedButton,
  OutlinedButtonProps,
} from "tamagui-config";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => Promise<void> | void;
  onConfirm: () => Promise<void> | void;
  title: string;
  titleProps?: HeadingProps;
  description?: string;
  descriptionProps?: ParagraphProps;
  confirmButtonText?: string;
  confirmButtonProps?: GradientButtonProps;
  cancelButtonText?: string;
  cancelButtonProps?: OutlinedButtonProps;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  titleProps,
  description,
  descriptionProps,
  confirmButtonText,
  confirmButtonProps,
  cancelButtonText,
  cancelButtonProps,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [loading, setIsLoading] = useState(false);

  const checkmarkGradientStart = getTokenValue(
    "$gradientSuccessCheckmarkStart" as Token,
    "color",
  );
  const checkmarkGradientEnd = getTokenValue(
    "$gradientSuccessCheckmarkEnd" as Token,
    "color",
  );

  const onConfirmPress = async () => {
    setIsLoading(true);
    await onConfirm();
    onOpenChange(false);
    setIsLoading(false);
  };

  const onCancelPress = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={480}
          maxWidth="95vw"
          maxHeight="90dvh"
          className="scroll-y-auto"
          gap="$5"
          alignItems="center"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          <Circle
            size={120}
            backgroundImage={`linear-gradient(231.68deg, ${checkmarkGradientStart} 34.15%, ${checkmarkGradientEnd} 64.88%)`}
            backgroundOrigin="border-box"
            borderWidth={2}
            borderColor="$transparentBorder"
          >
            <AlertCircle size={60} color="$warningRed" />
          </Circle>

          <YStack gap="$3.5">
            <Dialog.Title size="$15" lineHeight="$3"
              fontSize="$4"
              fontWeight="normal"
              textAlign="center"
              {...titleProps}
            >
              {title}
            </Dialog.Title>

            {description && (
              <Dialog.Description
                fontSize="$3"
                textAlign="center"
                color="$white10"
                {...descriptionProps}
              >
                {description}
              </Dialog.Description>
            )}
          </YStack>

          <XStack
            gap="$3.5"
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
          >
            <OutlinedButton
              rounded
              onPress={onCancelPress}
              {...cancelButtonProps}
            >
              {cancelButtonText ?? t("common.cancel")}
            </OutlinedButton>
            <GradientButton
              rounded
              loading={loading}
              onPress={onConfirmPress}
              {...confirmButtonProps}
            >
              {confirmButtonText ?? t("common.confirm")}
            </GradientButton>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default ConfirmDialog;
